
import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { setSentryUser } from "@/lib/sentry";


// Define a more comprehensive user profile type that includes company admin status and approval
type EnhancedUser = User & {
  is_admin?: boolean;
  is_company_admin?: boolean;
  approval_status?: 'pending_approval' | 'approved' | 'rejected' | 'pending_payment' | 'pending_manual_approval';
  is_active?: boolean;
};

interface AuthContextValue {
  user: EnhancedUser | null;
  realUser: EnhancedUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isCompanyAdmin: boolean;
  adminLoading: boolean;
  profileLoading: boolean;
  adminError: unknown;
  profileError: unknown;
  refreshAdminStatus: () => Promise<unknown>;
  refreshUserProfile: () => Promise<unknown>;
  approvalStatus: string;
  isActive: boolean;
  hasFullAccess: boolean;
  isImpersonating: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function useAuthInternal(): AuthContextValue {
  const [user, setUser] = useState<EnhancedUser | null>(null);
  const [realUser, setRealUser] = useState<EnhancedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const realUserRef = useRef<EnhancedUser | null>(null);
  const initialSignInHandledRef = useRef(false);
  const navigate = useNavigate();
  const { isImpersonating, impersonatedUserId, impersonatedUserData } = useImpersonation();

  useEffect(() => {
    realUserRef.current = realUser;
  }, [realUser]);

  const {
    data: adminData,
    isLoading: adminLoading,
    error: adminError,
    refetch: refetchAdminStatus
  } = useQuery({
    queryKey: ['user-admin-status', user?.id],
    queryFn: async () => {
      if (!user) return { is_admin: false };
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('is_admin_safe_check');
        if (rpcError) {
          console.error("Error in safe admin check:", rpcError);
          throw new Error(rpcError.message || 'Admin access check failed.');
        }
        return { is_admin: rpcData === true };
      } catch (error) {
        console.error("Unexpected error in admin status check:", error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const {
    data: profileData,
    refetch: refetchProfile,
    isLoading: profileLoading,
    error: profileError
  } = useQuery({
    queryKey: ['user-profile-data', isImpersonating ? impersonatedUserId : user?.id],
    queryFn: async () => {
      const targetUserId = isImpersonating ? impersonatedUserId : user?.id;
      if (!targetUserId) return null;
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('full_name, company_id, office_location_id, job_role, approval_status, is_active')
          .eq('id', targetUserId)
          .single();
        if (profileError) {
          console.error("Error fetching user profile:", profileError);
          throw new Error(profileError.message || 'Profile access check failed.');
        }
        const { data: roles, error: rolesError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', targetUserId);
        if (rolesError) {
          console.error("Error fetching user roles:", rolesError);
          throw new Error(rolesError.message || 'Role access check failed.');
        }
        const hasAdminRole = roles?.some(r => r.role === 'admin') || false;
        const hasCompanyAdminRole = roles?.some(r => r.role === 'company_admin') || false;
        return { ...profile, is_admin: hasAdminRole, is_company_admin: hasCompanyAdminRole };
      } catch (error) {
        console.error("Exception in profile data fetch:", error);
        throw error;
      }
    },
    enabled: !!(isImpersonating ? impersonatedUserId : user),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const refreshUserProfile = () => refetchProfile();
  const refreshAdminStatus = () => refetchAdminStatus();

  useEffect(() => {
    if (isImpersonating && impersonatedUserId && profileData) {
      setUser({
        id: impersonatedUserId,
        email: impersonatedUserData?.email || '',
        is_admin: profileData.is_admin === true,
        is_company_admin: profileData.is_company_admin === true,
        approval_status: profileData.approval_status,
        is_active: profileData.is_active,
        user_metadata: {
          full_name: profileData.full_name,
          company_id: profileData.company_id,
          office_location_id: profileData.office_location_id,
          job_role: profileData.job_role,
        },
        aud: 'authenticated',
        created_at: '',
        app_metadata: {},
      } as EnhancedUser);
    } else if (realUser && profileData && !isImpersonating) {
      setUser(currentUser => {
        if (!currentUser) return null;
        return {
          ...currentUser,
          is_admin: profileData.is_admin === true,
          is_company_admin: profileData.is_company_admin === true,
          approval_status: profileData.approval_status,
          is_active: profileData.is_active,
          user_metadata: {
            ...currentUser.user_metadata,
            full_name: currentUser.user_metadata?.full_name || profileData.full_name,
            company_id: profileData.company_id,
            office_location_id: profileData.office_location_id,
            job_role: profileData.job_role,
          },
        };
      });
    }
  }, [profileData, realUser, isImpersonating, impersonatedUserId, impersonatedUserData?.email]);

  // Sentry user context — id + role + company only. Never email or name.
  useEffect(() => {
    if (isImpersonating) return; // keep the real admin in Sentry while impersonating
    if (!realUser) {
      setSentryUser(null);
      return;
    }
    setSentryUser({
      id: realUser.id,
      company_id: (profileData?.company_id as string | undefined) ?? null,
      is_admin: profileData?.is_admin === true,
      is_company_admin: profileData?.is_company_admin === true,
    });
  }, [realUser, profileData, isImpersonating]);


  const isAdmin = user?.is_admin === true || profileData?.is_admin === true || adminData?.is_admin === true;
  const isCompanyAdmin = user?.is_company_admin === true || profileData?.is_company_admin === true;
  const approvalStatus = user?.approval_status || profileData?.approval_status || 'approved';
  const isActive = user?.is_active ?? profileData?.is_active ?? true;
  const hasFullAccess = approvalStatus === 'approved' && isActive;

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state change event:", event);

      if (event === "PASSWORD_RECOVERY") {
        if (!window.location.pathname.includes('reset-password')) {
          navigate('/reset-password');
        }
        setLoading(false);
        return;
      }

      if (event === "TOKEN_REFRESHED") {
        // Silent token refresh (also fires on tab refocus). Do NOT reset `user`
        // — that would strip the enriched profile fields and cause AuthGuard to
        // re-render, wiping any in-progress form state. Also skip refetching
        // the profile; React Query has a 5-minute staleTime for it.
        setRealUser(prev => prev ?? session?.user ?? null);
        setLoading(false);
        return;
      }

      if (event === "SIGNED_IN") {
        const isExistingSession = !!session?.user?.id && realUserRef.current?.id === session.user.id;
        if (isExistingSession && initialSignInHandledRef.current) {
          // Supabase can emit SIGNED_IN again when a backgrounded tab regains
          // focus. Treat that as a no-op so auth state is not replaced and
          // guarded pages do not flash back to their loading state.
          setLoading(false);
          return;
        }

        initialSignInHandledRef.current = true;
        setRealUser(session?.user ?? null);
        if (!isImpersonating) setUser(session?.user ?? null);
        setTimeout(() => { refreshUserProfile(); }, 500);

        if (session?.user) {
          // Record this login and capture the previous login time for display
          setTimeout(async () => {
            try {
              const { data: priorLogin, error: loginErr } = await supabase.rpc('record_user_login');
              if (loginErr) {
                console.error('record_user_login failed', loginErr);
              } else {
                sessionStorage.setItem('previousLoginAt', priorLogin ? String(priorLogin) : '');
                window.dispatchEvent(new Event('previousLoginAtUpdated'));
              }
            } catch (e) {
              console.error('record_user_login threw', e);
            }
          }, 0);

          setTimeout(() => {
            const checkAndRedirect = async () => {
              const { data: profile } = await supabase
                .from('profiles')
                .select('approval_status')
                .eq('id', session.user.id)
                .single();
              if (profile) {
                switch (profile.approval_status) {
                  case 'pending_payment': {
                    const { data: pendingReg } = await supabase
                      .from('pending_registrations')
                      .select('registration_token')
                      .eq('user_id', session.user.id)
                      .eq('completed', false)
                      .single();
                    if (pendingReg?.registration_token) {
                      navigate(`/complete-registration?token=${pendingReg.registration_token}`);
                    }
                    break;
                  }
                  case 'pending_manual_approval':
                    navigate('/registration-pending?type=manual-approval');
                    break;
                  case 'pending_approval':
                    navigate('/registration-pending?type=company-approval');
                    break;
                }
              }
            };
            checkAndRedirect();
          }, 1000);
        }
        setLoading(false);
      } else if (event === "SIGNED_OUT") {

        initialSignInHandledRef.current = false;
        setUser(null);
        setRealUser(null);
        setLoading(false);
      } else if (event === "USER_UPDATED") {
        setRealUser(session?.user ?? null);
        if (!isImpersonating) setUser(session?.user ?? null);
        setTimeout(() => { refreshUserProfile(); }, 500);
        setLoading(false);
      } else if (event === "INITIAL_SESSION") {
        // session already loaded by checkSession
      }
    });

    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) console.error("Error checking session:", error);
        if (session?.user) {
          setRealUser(session.user);
          if (!isImpersonating) setUser(session.user);
          initialSignInHandledRef.current = true;
          setTimeout(() => { refreshUserProfile(); }, 500);
        } else {
          setUser(null);
          setRealUser(null);
        }
        setLoading(false);
      } catch (error) {
        console.error("Exception checking session:", error);
        setUser(null);
        setLoading(false);
      }
    };

    checkSession();

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = async () => {
    try {
      setLoading(true);
      localStorage.removeItem('supabase.auth.token');
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        console.error("Error signing out:", error);
        throw error;
      }
      setUser(null);
      navigate("/logout-confirmation");
    } catch (error) {
      console.error("Error in signOut function:", error);
      navigate("/logout-confirmation");
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    realUser,
    loading,
    signOut,
    isAdmin,
    isCompanyAdmin,
    adminLoading,
    profileLoading,
    adminError,
    profileError,
    refreshAdminStatus,
    refreshUserProfile,
    approvalStatus,
    isActive,
    hasFullAccess,
    isImpersonating,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const value = useAuthInternal();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
